import * as React from 'react'
import IconButton from '@material-ui/core/IconButton'
import PhotoIcon from '@material-ui/icons/AddAPhoto'
import VideocamIcon from '@material-ui/icons/Videocam'
import { logError } from '../../logging'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import TakePhotoIcon from '@material-ui/icons/PhotoCamera'
import PhotoLibrary from '@material-ui/icons/PhotoLibrary'
import {
  getFileData,
  getRecordedVideoFile,
  takeFromCamera
} from './utils/cordovaPhotos'
import { processPhoto } from './utils/processPhoto'
import {
  createVideoPhoto,
  createVideoPhotoFromFile
} from './utils/imageUtils'
import { withOneplaceLibraryContext } from '../OneplaceLibraryProvider'
import {
  Theme,
  createStyles,
  withStyles,
  WithStyles
} from '@material-ui/core/styles'
import { IAddPhotoButtonProps } from './AddPhotoButton'
import { IBasicDialogProps, BasicDialog } from '../common/BasicDialog'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import { CONFIG } from '../../config'
import { IPhoto } from '../../models/Checklist'

const styles = (theme: Theme) =>
  createStyles({
    mandatoryComment: {
      color: theme.palette.secondary.main
    },
    uploadInput: {
      opacity: 0,
      position: 'absolute',
      zIndex: -10,
      right: 0
    }
  })

export interface IAddPhotoButtonCordovaState {
  photoMenuAnchor: any
  dialogProps: IBasicDialogProps
}

export interface IAddPhotoButtonCordovaProps
  extends IAddPhotoButtonProps,
    WithStyles<typeof styles> {}

export const AddPhotoButtonCordova = withStyles(styles)(
  withOneplaceLibraryContext(
    class extends React.Component<
      IAddPhotoButtonCordovaProps,
      IAddPhotoButtonCordovaState
    > {
      public static defaultProps = {
        allowPhotosFromGallery: true
      }
      static displayName = 'AddPhotoButtonCordova'
      private fileInputRef: React.RefObject<HTMLInputElement>
      constructor(props: any) {
        super(props)
        this.fileInputRef = React.createRef()
        this.addPhotoButtonClick = this.addPhotoButtonClick.bind(this)
        this.photoMenuClose = this.photoMenuClose.bind(this)
        this.takeFromCamera = this.takeFromCamera.bind(this)
        this.takeFromGallery = this.takeFromGallery.bind(this)
        this.state = {
          photoMenuAnchor: null,
          dialogProps: {
            content: <></>,
            isOpen: false,
            onClose: () => null,
            disableBackdropClick: true
          }
        }
      }

      addPhotoButtonClick(event: any): void {
        if (this.props.disabled) return
        this.setState({ photoMenuAnchor: event.currentTarget })
      }

      photoMenuClose(): void {
        this.setState({ photoMenuAnchor: null })
      }

      async takeFromCamera(): Promise<string> {
        let error = false
        let validationError = false
        let updateRequiredError = false
        try {
          if (this.props.videoOnly) {
            await this.recordVideo()
          } else {
            const settings = await this.props.ctx.client.getSettings()
            const photo = await takeFromCamera(
              settings.addPhotosToGallery,
              this.props.imageStorage,
              this.props.ctx.env
            )
            if (photo) {
              this.props.onPhotoUploaded([photo])
            }
          }
        } catch (e) {
          logError(
            e,
            this.props.videoOnly
              ? 'Error while recording video'
              : 'Error while processing image from camera'
          )
          validationError = this.isVideoSizeValidationError(e)
          updateRequiredError = this.isVideoUpdateRequiredError(e)
          error = !validationError && !updateRequiredError
        }
        this.photoMenuClose()
        if (validationError) {
          this.showValidationDialog()
        } else if (updateRequiredError) {
          this.showVideoUpdateDialog()
        } else if (error) {
          this.showUploadErrorDialog()
        }
        return ''
      }

      recordVideo = (): Promise<void> => {
        return new Promise((resolve, reject) => {
          getRecordedVideoFile()
            .then(async (video) => {
              if (!video) {
                resolve()
                return
              }

              try {
                const fileData = await getFileData(
                  video.filePath,
                  video.contentType
                )

                const maxSize = this.props.maxFileSize || CONFIG.videoMaxFileSize
                if (fileData.size / 1024 / 1024 > maxSize) {
                  reject(
                    new Error(
                      `Video exceeds maximum size of ${maxSize} MB.`
                    )
                  )
                  return
                }

                const photo = createVideoPhoto(
                  fileData,
                  video.fileName,
                  'camera',
                  video.contentType
                )
                this.props.onPhotoUploaded([photo])
                resolve()
              } catch (error) {
                reject(error)
              }
            })
            .catch((error) => reject(error))
        })
      }

      isVideoSizeValidationError = (error: unknown): boolean => {
        return Boolean(
          this.props.videoOnly &&
          error instanceof Error &&
          error.message.includes('maximum size')
        )
      }

      isVideoUpdateRequiredError = (error: unknown): boolean => {
        return Boolean(
          this.props.videoOnly &&
          error instanceof Error &&
          error.message.includes('Please update the app')
        )
      }

      async takeFromGallery(event: any) {
        if (this.props.disabled) return
        this.photoMenuClose()
        const files: FileList = event.target.files
        let error = false
        if (files && files[0]) {
          try {
            if (!this.validateFiles(files)) {
              this.showValidationDialog()
              return
            }

            const photos: IPhoto[] = []

            if (this.props.videoOnly) {
              for (let i = 0; i < files.length; i++) {
                photos.push(createVideoPhotoFromFile(files[i]))
              }
            } else {
              this.showLoadingImagesDialog(files.length)

              for (let i = 0; i < files.length; i++) {
                const photo = await processPhoto(
                  files[i],
                  files[i].name,
                  this.props.ctx.imageStorage
                )
                photo.attachmentType = 'photo'
                photo.source = 'gallery'
                photos.push(photo)
                this.updateLoadingProgress(photos.length, files.length)
              }
            }

            this.props.onPhotoUploaded(photos)
          } catch (e) {
            logError(
              e,
              this.props.videoOnly
                ? 'Error while selecting video'
                : 'Error while processing uploaded image'
            )
            error = true
          }
        }

        if (this.fileInputRef.current) {
          /*this allows the same file to be uploaded twice
          (chrome only add the file when the field changes)*/
          this.fileInputRef.current.value = ''
        }
        this.hideDialog()
        if (error) {
          this.showUploadErrorDialog()
        }
      }

      showFileGallery = () => {
        if (this.props.disabled) return
        this.fileInputRef.current?.click()
      }

      hideDialog = () => {
        this.setState({
          dialogProps: {
            ...this.state.dialogProps,
            isOpen: false
          }
        })
      }

      showLoadingImagesDialog = (total: number) => {
        this.setState({
          dialogProps: {
            ...this.state.dialogProps,
            isOpen: true,
            title: `Loading Images... (0 of ${total})`,
            content: (
              <div style={{ textAlign: 'center', width: '100%' }}>
                <CircularProgress color='primary' disableShrink={true} />
              </div>
            ),
            showCloseButton: false
          }
        })
      }

      showValidationDialog = () => {
        const maxSize = this.props.maxFileSize || (this.props.videoOnly ? CONFIG.videoMaxFileSize : CONFIG.imageMultipleMaxSize)
        const reRecord = () => {
          this.hideDialog()
          void this.takeFromCamera()
        }
        this.setState({
          dialogProps: {
            ...this.state.dialogProps,
            isOpen: true,
            title: this.props.videoOnly ? 'Video too large' : 'Validation Error',
            content: this.props.videoOnly ? (
              <>
                <Typography variant='body1'>
                  This video is larger than the {maxSize} MB upload limit. Please re-record a shorter clip.
                </Typography>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <Button color='primary' onClick={this.hideDialog}>Cancel</Button>
                  <Button color='primary' variant='contained' onClick={reRecord}>Re-record</Button>
                </div>
              </>
            ) : (
              <>
                {`The limit for uploading photos at once is
                ${CONFIG.imageMultipleMaxNumber} photos or
                ${CONFIG.imageMultipleMaxSize} MB`}
              </>
            ),
            showCloseButton: !this.props.videoOnly,
            onClose: this.hideDialog
          }
        })
      }

      showUploadErrorDialog = () => {
        this.setState({
          dialogProps: {
            ...this.state.dialogProps,
            isOpen: true,
            title: this.props.videoOnly
              ? 'Error processing video'
              : 'Error processing image',
            content: this.props.videoOnly ? (
              <>
                <Typography variant='body1'>
                  We are unable to process this video. Please try again with a
                  supported video format (MP4, MOV, WebM).
                </Typography>
              </>
            ) : (
              <>
                <Typography variant='body1'>
                  We are unable to process this image. Our system supports only
                  JPG and PNG file formats. Below are some possible reasons why
                  this error occurred:
                </Typography>
                <ul>
                  <li>
                    The image is corrupted in some way that prevents it from
                    being loaded.
                  </li>
                  <li>
                    The image format is not supported. eg you uploaded a High
                    Efficiency Image Format file (HEIF)
                  </li>
                </ul>
              </>
            ),
            showCloseButton: true,
            onClose: this.hideDialog
          }
        })
      }

      showVideoUpdateDialog = () => {
        this.setState({
          dialogProps: {
            ...this.state.dialogProps,
            isOpen: true,
            title: 'Update Required',
            content: (
              <Typography variant='body1'>
                This version of the mobile app does not support native video
                capture. Please update the app from the App Store or Play Store
                and try again.
              </Typography>
            ),
            showCloseButton: true,
            onClose: this.hideDialog
          }
        })
      }

      validateFiles = (files: FileList): boolean => {
        if (this.props.videoOnly) {
          const maxSize = this.props.maxFileSize || CONFIG.videoMaxFileSize
          for (let i = 0; i < files.length; i++) {
            if (files[i].size / 1024 / 1024 > maxSize) {
              return false
            }
          }
          return true
        }

        if (files.length > CONFIG.imageMultipleMaxNumber) {
          return false
        }

        let size = 0
        for (let i = 0; i < files.length; i++) {
          size += files[i].size
        }
        if (
          size / 1024 / 1024 >
          (this.props.maxFileSize || CONFIG.imageMultipleMaxSize)
        ) {
          return false
        }
        return true
      }

      updateLoadingProgress = (loaded: number, total: number) => {
        this.setState({
          dialogProps: {
            ...this.state.dialogProps,
            title: `Loading Images... (${loaded} of ${total})`
          }
        })
      }

      render(): React.ReactNode {
        const isVideo = this.props.videoOnly
        const ButtonIcon = isVideo ? VideocamIcon : PhotoIcon
        const cameraLabel = isVideo
          ? this.props.ctx.i18next.t('record_video')
          : this.props.ctx.i18next.t('take_a_photo')
        const galleryLabel = isVideo
          ? this.props.ctx.i18next.t('select_video')
          : this.props.ctx.i18next.t('select_from_gallery')
        const fileAccept = isVideo
          ? 'video/*'
          : 'image/jpeg, image/png, image/gif'

        const videoMaxSize = this.props.maxFileSize || CONFIG.videoMaxFileSize
        const menuItems = [
          isVideo && (
            <div
              key='video-size-hint'
              style={{ padding: '8px 16px', pointerEvents: 'none' }}
            >
              <Typography variant='caption' color='textSecondary'>
                Max file size is {videoMaxSize} MB (Please keep to under 30s)
              </Typography>
            </div>
          ),
          <MenuItem
            onClick={this.takeFromCamera}
            key='takeFromCamera'
            id={this.props.controlId + '-photobutton-takefromcamera'}
            className={this.props.className}
            disabled={this.props.disabled}
          >
            <ListItemIcon className={this.props.className}>
              {isVideo ? <VideocamIcon /> : <TakePhotoIcon />}
            </ListItemIcon>
            <ListItemText primary={cameraLabel} />
          </MenuItem>,
          this.props.allowPhotosFromGallery && (
            <MenuItem
              onClick={this.showFileGallery}
              key='showFileGallery'
              id={this.props.controlId + '-photobutton-showfilegallery'}
              className={this.props.className}
              disabled={this.props.disabled}
            >
              <ListItemIcon className={this.props.className}>
                <PhotoLibrary />
              </ListItemIcon>
              <ListItemText primary={galleryLabel} />
            </MenuItem>
          )
        ]

        const iconOnly =
          this.props.standalone != null ? this.props.standalone : true
        return (
          <React.Fragment>
            {iconOnly ? (
              <IconButton
                id={this.props.controlId + '-photobutton'}
                className={this.props.className}
                onClick={this.addPhotoButtonClick}
                disabled={this.props.disabled}
                style={this.props.style}
              >
                <ButtonIcon />
              </IconButton>
            ) : (
              menuItems
            )}
            <Menu
              anchorEl={this.state.photoMenuAnchor}
              open={Boolean(this.state.photoMenuAnchor)}
              onClose={this.photoMenuClose}
            >
              {menuItems}
            </Menu>
            <input
              data-testid={this.props.controlId + '-photo-upload'}
              multiple={this.props.allowMultipleFiles !== false}
              ref={this.fileInputRef}
              type='file'
              accept={fileAccept}
              id={this.props.controlId + '-photo-upload'}
              className={this.props.classes.uploadInput}
              onChange={this.takeFromGallery}
            />
            <BasicDialog {...this.state.dialogProps} />
          </React.Fragment>
        )
      }
    }
  )
)
